// Bulk-import format helpers for the Manufacturing → Inventory bulk modal.
//
// Two formats are supported:
//   - JSON: a top-level array of row objects (strict JSON, e.g. `JSON.parse`-able).
//   - CSV: header row + value rows, RFC 4180-ish (double quotes, embedded
//     commas, escaped `""`, CR/LF tolerated). Numeric / boolean coercion is
//     left to the server-side validator (which already handles string-numeric
//     and string-boolean), so CSV cells just go through as strings.
//
// `parseBulkImport` returns `{ rows: object[] }` on success; it does NOT enforce
// schema. Schema enforcement happens in the same place on both client + server
// (the validator in `server/mfg-domain/bulkImportValidator.ts` for the server,
// and a mirror in the bulk modal for the client preview).

export type BulkImportFormat = "json" | "csv";

export type BulkImportParseResult =
  | { ok: true; format: BulkImportFormat; rows: Array<Record<string, unknown>> }
  | { ok: false; error: string };

const WHITESPACE = /\s/;

export function detectBulkImportFormat(text: string): BulkImportFormat {
  for (const ch of text) {
    if (WHITESPACE.test(ch)) continue;
    if (ch === "[" || ch === "{") return "json";
    return "csv";
  }
  // All-whitespace input — treat as CSV so the empty-input message comes from
  // the schema validator (which has nicer copy than a JSON parser error).
  return "csv";
}

export function parseBulkImport(text: string, formatHint?: BulkImportFormat): BulkImportParseResult {
  const trimmed = text.trim();
  if (!trimmed) return { ok: false, error: "Empty input — paste rows or load a file." };

  const format = formatHint ?? detectBulkImportFormat(trimmed);

  if (format === "json") {
    let parsed: unknown;
    try {
      parsed = JSON.parse(trimmed);
    } catch (e) {
      return {
        ok: false,
        error: `JSON parse error: ${e instanceof Error ? e.message : String(e)}`,
      };
    }
    if (!Array.isArray(parsed)) {
      return { ok: false, error: "Expected a top-level JSON array of row objects." };
    }
    const rows = parsed.filter(
      (r): r is Record<string, unknown> => typeof r === "object" && r !== null && !Array.isArray(r)
    );
    return { ok: true, format: "json", rows };
  }

  // CSV path.
  const grid = parseCsvRows(trimmed);
  if (grid.length === 0) return { ok: false, error: "CSV had no rows." };
  const [headerRow, ...dataRows] = grid;
  const headers = headerRow.map((h) => h.trim());
  const rows: Array<Record<string, unknown>> = [];
  for (const row of dataRows) {
    if (row.every((cell) => cell.trim() === "")) continue;
    const obj: Record<string, unknown> = {};
    headers.forEach((h, i) => {
      if (!h) return;
      const cell = row[i];
      if (cell === undefined) return;
      obj[h] = cell;
    });
    rows.push(obj);
  }
  return { ok: true, format: "csv", rows };
}

/**
 * Tokenizes a CSV document into a 2D string array. Handles:
 *   - Quoted fields with embedded commas / newlines.
 *   - Escaped quotes inside quoted fields ("she said ""hi""").
 *   - CRLF, CR, and LF line endings.
 *
 * Out of scope: multi-character delimiters, header rows that themselves
 * contain newlines (rare for ERP exports), comment lines.
 */
export function parseCsvRows(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = "";
  let inQuotes = false;
  let i = 0;

  while (i < text.length) {
    const ch = text[i];

    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          cell += '"';
          i += 2;
          continue;
        }
        inQuotes = false;
        i++;
        continue;
      }
      cell += ch;
      i++;
      continue;
    }

    if (ch === '"') {
      inQuotes = true;
      i++;
      continue;
    }
    if (ch === ",") {
      row.push(cell);
      cell = "";
      i++;
      continue;
    }
    if (ch === "\r") {
      i++;
      continue;
    }
    if (ch === "\n") {
      row.push(cell);
      rows.push(row);
      row = [];
      cell = "";
      i++;
      continue;
    }
    cell += ch;
    i++;
  }

  if (cell !== "" || row.length > 0) {
    row.push(cell);
    rows.push(row);
  }
  return rows;
}
